import { Capacitor } from '@capacitor/core';
import { LiveActivity, GolfGameLiveData } from '../plugins/live-activity';

// Get the plugin if available
const getLiveActivityPlugin = () => {
  // Check if we're on iOS first
  const isIOS = Capacitor.getPlatform() === 'ios' && Capacitor.isNativePlatform();
  if (!isIOS) {
    return null;
  }

  // Debug all available plugins
  const availablePlugins = Object.keys((Capacitor as any).Plugins);

  // Direct access to the plugin instead of using isPluginAvailable
  // This is more reliable for native plugins
  if (availablePlugins.includes('MGPLiveActivity') && Capacitor.isNativePlatform()) {
    console.log('MGPLiveActivity plugin is available!');
    return LiveActivity;
  }

  console.log('Live Activity plugin not available - make sure you\'re on iOS 16.1+ and running on a physical device');

  return null;
};

// Service for managing Live Activities
class LiveActivityService {
  private activeActivityId: string | null = null;

  /**
   * Start a new Live Activity for the current golf game
   * @param gameData Golf game data to display in the Live Activity
   * @returns Promise with the activity ID if successful
   */
  async startLiveActivity(gameData: GolfGameLiveData): Promise<string | null> {
    const plugin = getLiveActivityPlugin();

    if (!plugin) {
      return null;
    }

    try {
      console.log('Starting Live Activity with data:', JSON.stringify(gameData));
      const result = await plugin.startLiveActivity({ gameData });
      this.activeActivityId = result.activityId;
      console.log('Live Activity started successfully with ID:', result.activityId);
      return result.activityId;
    } catch (error) {
      console.error('CapGolf JS - Error starting Live Activity:', error);
      return null;
    }
  }

  /**
   * Update an existing Live Activity with new game data
   * @param gameData Updated golf game data
   * @param activityId Optional activity ID (uses stored ID if not provided)
   * @returns Promise that resolves when update is complete
   */
  async updateLiveActivity(gameData: GolfGameLiveData, activityId?: string): Promise<boolean> {
    const plugin = getLiveActivityPlugin();
    const targetId = activityId || this.activeActivityId;

    if (!plugin || !targetId) {
      console.log('Live Activity plugin not available or no active activity');
      return false;
    }

    try {
      await plugin.updateLiveActivity({
        activityId: targetId,
        gameData
      });
      return true;
    } catch (error) {
      console.error('Error updating Live Activity:', error);
      return false;
    }
  }

  /**
   * End an existing Live Activity
   * @param activityId Optional activity ID (uses stored ID if not provided)
   * @returns Promise that resolves when activity is ended
   */
  async endLiveActivity(activityId?: string): Promise<boolean> {
    const plugin = getLiveActivityPlugin();
    const targetId = activityId || this.activeActivityId;

    if (!plugin || !targetId) {
      console.log('Live Activity plugin not available or no active activity');
      return false;
    }

    try {
      await plugin.endLiveActivity({ activityId: targetId });
      if (targetId === this.activeActivityId) {
        this.activeActivityId = null;
      }
      return true;
    } catch (error) {
      console.error('Error ending Live Activity:', error);
      return false;
    }
  }

  /**
   * Get all active Live Activities
   * @returns Promise with array of activity IDs
   */
  async getAllActivities(): Promise<string[]> {
    const plugin = getLiveActivityPlugin();

    if (!plugin) {
      console.log('Live Activity plugin not available');
      return [];
    }

    try {
      const result = await plugin.getAllActivities();
      return result.activityIds;
    } catch (error) {
      console.error('Error getting Live Activities:', error);
      return [];
    }
  }

  /**
   * Get the ID of the currently active Live Activity
   * @returns Current activity ID or null if none active
   */
  getActiveActivityId(): string | null {
    return this.activeActivityId;
  }
}

// Export a singleton instance
export const liveActivityService = new LiveActivityService();
